/**
 * Parking-lot grid tile types (spec section 8).
 *
 * Modeled as a string union (via a const object) rather than a TS enum so the
 * values serialize as stable, human-readable strings in saves and survive a
 * JSON round-trip cleanly (works with isolatedModules).
 */
export const TILE = {
  Empty: 'empty',
  Road: 'road',
  ParkingSpace: 'parking',
  Entrance: 'entrance',
  Exit: 'exit',
  Barrier: 'barrier',
  Cashier: 'cashier',
  PaymentMachine: 'payment',
  Office: 'office',
  CCTV: 'cctv',
  EVCharger: 'ev',
  DisabledParking: 'disabled',
  MotorcycleParking: 'moto',
  Decoration: 'deco',
} as const;

export type TileType = (typeof TILE)[keyof typeof TILE];

/** Render colors for each tile type (temporary art — spec section 43). */
export const TILE_COLORS: Record<TileType, number> = {
  [TILE.Empty]: 0x2e7d32, // grass green
  [TILE.Road]: 0x37474f, // dark asphalt
  [TILE.ParkingSpace]: 0x9e9e9e, // gray
  [TILE.Entrance]: 0x43a047, // green gate
  [TILE.Exit]: 0xe53935, // red gate
  [TILE.Barrier]: 0x8d6e63,
  [TILE.Cashier]: 0xfbc02d, // amber booth
  [TILE.PaymentMachine]: 0xffb300,
  [TILE.Office]: 0x5d4037,
  [TILE.CCTV]: 0x546e7a,
  [TILE.EVCharger]: 0x00acc1,
  [TILE.DisabledParking]: 0x1e88e5,
  [TILE.MotorcycleParking]: 0x7cb342,
  [TILE.Decoration]: 0x66bb6a,
};

/** Tile types that count toward parking capacity. */
export const CAPACITY_TILES: ReadonlySet<TileType> = new Set<TileType>([
  TILE.ParkingSpace,
  TILE.DisabledParking,
  TILE.MotorcycleParking,
]);
